/**
 * ImportTemplateCard
 *
 * Special first card in the Templates gallery grid that opens the
 * "Import from JSON" view. Lets users load a generated AutoTemplate
 * manifest.json (e.g. from the monitoring view's Generate Template flow)
 * and test it in the gallery before contributing it to the public
 * templates repo.
 */

import { memo, useCallback } from 'react';
import { makeStyles, tokens, Text } from '@coreai/fluentui-react';
import { ArrowUpload24Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px dashed ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
    cursor: 'pointer',
    textAlign: 'center',
    transitionProperty: 'background-color, box-shadow, border-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2Hover,
      boxShadow: tokens.shadow4,
      border: `1px dashed ${tokens.colorBrandStroke1}`,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },
    ':active': {
      backgroundColor: tokens.colorNeutralBackground2Pressed,
    },
  },
  icon: {
    color: tokens.colorBrandForeground1,
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
});

export interface ImportTemplateCardProps {
  onClick: () => void;
}

export const ImportTemplateCard: React.FC<ImportTemplateCardProps> = memo(({ onClick }) => {
  const styles = useStyles();

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    },
    [onClick]
  );

  return (
    <div
      className={styles.card}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      aria-label="Import a template from a JSON file"
    >
      <ArrowUpload24Regular className={styles.icon} aria-hidden="true" />
      <Text className={styles.title}>Import from JSON</Text>
      <Text className={styles.subtitle}>Test a generated template file</Text>
    </div>
  );
});

ImportTemplateCard.displayName = 'ImportTemplateCard';
